import React, { KeyboardEventHandler, useCallback, useState } from 'react';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { IconButton, TextField, Toolbar, useMediaQuery } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/SearchTwoTone';
import ClearIcon from '@material-ui/icons/ClearTwoTone';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { FavoriteBorder, ListTwoTone } from '@material-ui/icons';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import { useTranslate } from '@sprinx/react-globalize';
import Page from '../../@sprinx/react-web/Page';
import { getFavoriteProducts } from '../../api/products/favoriteProducts';
import CataloguePageListContent from '../CataloguePage/components/CataloguePageListContent';
import CataloguePageListContentSkeleton from '../CataloguePage/components/CataloguePageListContentSkeleton';
import CataloguePageListToolbarViewSwitcher from '../CataloguePage/components/CataloguePageListToolbarViewSwitcher';
import PageHeadline from '../../@sprinx/react-web/PageHeadline';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import StyledBreadcrumb from '../../components/StyledBreadcrumb';
import useRouteUrlFactory from '../../hooks/useRouteUrlFactory';
import { CatalogueProduct } from '../../api/products/catalogue';
import { MoleculerListResult } from '../../@sprinx/knihovka-types';
import usePromise from '../../hooks/usePromise';
import {
  apiClientState,
  currencyState,
  decimalPrecisionState,
  localeState,
  pricesTypeState,
  regionState,
} from '../../api/appState';
import { SearchMobile } from '../../layouts/MainLayout/components/AppHeader';

export type FavoriteProductsPageLayoutProps = StandardDivProps<FavoriteProductsPageLayoutClassKey>;

export type FavoriteProductsPageLayoutClassKey =
  | 'root'
  | 'list'
  | 'toolbar'
  | 'search'
  | 'searchMobile'
  | 'input'
  | 'textField'
  | 'startIcon'
  | 'endIcon';

const themeSettings = { name: 'FavoriteProductsPageLayout' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<FavoriteProductsPageLayoutClassKey, {}>({
      root: {},
      list: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        position: 'relative',
      },
      toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(0, 1),
        },
      },
      search: {
        position: 'absolute',
        left: '50%',
        margin: '0',
        width: '340px',
        zIndex: 1202,
        [theme.breakpoints.up('md')]: {
          transform: 'translateX(-50%)',
        },
      },
      searchMobile: {
        display: 'none',
        [theme.breakpoints.down('lg')]: {
          backgroundColor: 'inherit',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        },
      },
      input: {
        color: 'black',
        '&:focus': {
          color: 'black',
        },
      },
      textField: {
        '& input[type="search"]::-webkit-search-decoration, input[type="search"]::-webkit-search-cancel-button, input[type="search"]::-webkit-search-results-button, input[type="search"]::-webkit-search-results-decoration':
          { display: 'none' },
        '& label + .MuiInput-formControl': {
          paddingLeft: theme.spacing(6),
          color: 'black',
          '&:hover:before': {
            borderBottom: '1px solid black',
          },
          '&:before': {
            borderBottom: '1px solid black',
          },
        },
        '& label': {
          paddingLeft: theme.spacing(6),
          color: 'black!important',
        },
        '& .MuiInput-underline:after': {
          borderBottom: '1px solid black',
        },
        '@media (max-width:850px)': {
          position: 'fixed',
          left: 0,
          top: '60px',
          background: theme.palette.mainNavigation.background,
          paddingRight: theme.spacing(10),
        },
      },
      startIcon: {
        cursor: 'pointer',
        position: 'absolute',
        left: 0,
        top: theme.spacing(3),
        backgroundColor: 'none',
        fill: 'black',
        '@media (max-width: 850px)': {
          display: 'none',
        },
      },
      endIcon: {
        position: 'absolute',
        right: 0,
        top: theme.spacing(2),
        backgroundColor: 'inherit',
        '& svg': {
          fill: '#fafafa',
        },
        '@media (max-width: 850px)': {
          position: 'fixed',
          top: '68px',
          right: '5px',
        },
      },
    }),
  themeSettings,
);

const FavoriteProductsPageLayout = React.forwardRef<HTMLDivElement, FavoriteProductsPageLayoutProps>(
  ({ className, classes: pClasses, ...rest }, ref) => {
    const t = useTranslate();
    const routeUrlFactory = useRouteUrlFactory();
    const classes = useStyles({ classes: pClasses });
    const theme = useTheme();
    const [searchFilter, setSearchFilter] = React.useState<string>('');
    const [reloadSearch, setReloadSearch] = useState<number>(0); // counter to re-fetch favorite products
    const isBelowMedium = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const handleClick = React.useCallback(() => setMobileSearchOpen((v) => !v), []);

    const apiClient = useRecoilValue(apiClientState);
    const locale = useRecoilValue(localeState);
    const currency = useRecoilValue(currencyState);
    const decimalPrecision = useRecoilValue(decimalPrecisionState);
    const pricesType = useRecoilValue(pricesTypeState);
    const region = useRecoilValue(regionState);

    const res = usePromise<MoleculerListResult<CatalogueProduct>, unknown>(
      () =>
        getFavoriteProducts(apiClient, {
          locale,
          currency,
          decimalPrecision,
          enabledDynamicRules: undefined,
          pricesType,
          region,
          searchByName: searchFilter || undefined,
        }),
      undefined,
      [reloadSearch],
    ); // recoil does not work in more windows...

    const filterOnChange = (e: any) => {
      const value = e.target.value;
      setSearchFilter(value);
    };

    const handleResetSearchText = () => {
      setSearchFilter('');
      setReloadSearch((prev) => prev + 1);
    };

    const handleSearch = useCallback(() => {
      setReloadSearch((prev) => prev + 1);
    }, []);

    const handleSearchOnEnter = useCallback<KeyboardEventHandler<HTMLInputElement>>(
      (k) => {
        if (k.key === 'Enter' || k.keyCode === 13 || k.which === 13) {
          handleSearch();
          setMobileSearchOpen(false);
        }
      },
      [handleSearch],
    );

    return (
      <Page
        ref={ref}
        className={clsx(classes.root, className)}
        pageTitle={t('favoriteProductsPage/title')}
        breadcrumbs={
          <AppBreadcrumbs disableHome>
            <StyledBreadcrumb
              to={routeUrlFactory('catalogue')}
              label={t('cataloguePage/title')}
              icon={<ListTwoTone fontSize='small' />}
            />
            <StyledBreadcrumb
              to={routeUrlFactory('favoriteProducts')}
              label={t('favoriteProductsPage/title')}
              icon={<FavoriteBorder fontSize='small' />}
            />
          </AppBreadcrumbs>
        }
        {...rest}
      >
        <PageHeadline ref={ref}>{t('favoriteProductsPage/title')}</PageHeadline>
        <div className={classes.list} datatype='weeee'>
          {(!isBelowMedium || mobileSearchOpen) && (
            <div className={classes.search}>
              <TextField
                margin='dense'
                fullWidth
                label={t('app/favoriteItemsSearchPlaceholder')}
                value={searchFilter}
                className={classes.textField}
                onChange={filterOnChange}
                onKeyUp={handleSearchOnEnter}
                inputProps={{
                  type: 'search',
                  enterkeyhint: 'done',
                }}
              />
              <SearchIcon color='primary' fontSize='default' onClick={handleSearch} className={classes.startIcon} />

              {searchFilter.length > 0 && (
                <IconButton onClick={handleResetSearchText} className={classes.endIcon}>
                  <ClearIcon fontSize='small' />
                </IconButton>
              )}
            </div>
          )}
          {isBelowMedium && (
            <SearchMobile mobileSearchOpen={mobileSearchOpen} onClick={handleClick} className={classes.searchMobile} />
          )}

          <Toolbar ref={ref} className={classes.toolbar}>
            <CataloguePageListToolbarViewSwitcher />
          </Toolbar>
          {res.isLoading && <CataloguePageListContentSkeleton />}
          {!res.isLoading && res.data && <CataloguePageListContent catalogue={res.data} />}
        </div>
      </Page>
    );
  },
);

FavoriteProductsPageLayout.displayName = 'FavoriteProductsPageLayout';

export default withThemeProps(themeSettings)(FavoriteProductsPageLayout);
